@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginetabular
import org.example.project.utils.All
import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.project.enginekalender.EngineMonthCalendar
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaCheckbox
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.settings.applyAppButtonActiveChrome
import org.example.project.settings.applyAppButtonChrome
import org.example.project.settings.applyAppButtonHoverChrome
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.InputType
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.MouseEvent

object TableStyles : StyleSheet() {

    val tableWrapper by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        padding(8.px)
        gap(8.px)
        overflow("hidden")
        backgroundColor(Color.transparent)
        height(100.percent)
        property("box-sizing", "border-box")
    }

    val tableScrollFrame by style {
        flex(1)
        width(100.percent)
        overflowY("auto")
        overflowX("auto")
        borderRadius(12.px)
        property("border", "1px solid rgba(255,255,255,0.08)")
        property("box-shadow", "0 10px 26px rgba(0,0,0,0.14)")
        property("overscroll-behavior", "contain")
        property("scroll-snap-type", "none")
        property("scrollbar-width", "thin")
        property("scrollbar-color", "rgba(56, 62, 74, 0.98) transparent")

        self + "::-webkit-scrollbar" style {
            width(12.px)
            height(12.px)
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-track" style {
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-thumb" style {
            borderRadius(999.px)
            property("background", "linear-gradient(180deg, rgba(72, 79, 92, 0.98) 0%, rgba(46, 52, 62, 0.98) 100%)")
            property("border", "1px solid rgba(255,255,255,0.10)")
        }

        self + "::-webkit-scrollbar-corner" style {
            backgroundColor(Color.transparent)
        }
    }

    val smartScrollFrame by style {
        property("border", "1px solid transparent")
        property("box-shadow", "none")
        borderRadius(0.px)
        backgroundColor(Color.transparent)
    }

    val table by style {
        property("border-collapse", "separate")
        property("border-spacing", "0")
        property("min-width", "100%")
        property("table-layout", "fixed")
        property("user-select", "none")
        border(0.px, LineStyle.None, Color.transparent)
        fontSize(13.px)
        color(Color.white)
    }

    val th by style {
        backgroundColor(Color("#40454D"))
        property("border-bottom", "1px solid rgba(255,255,255,0.1)")
        property("border-right", "1px solid rgba(255,255,255,0.1)")
        fontWeight("bold")
        textAlign("center")
        padding(4.px, 8.px)
        whiteSpace("nowrap")
        height(32.px)
        position(Position.Sticky)
        top(0.px)
        property("z-index", "10")
        property("box-sizing", "border-box")
        color(Color.white)
        cursor("grab")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }

    val smartTh by style {
        backgroundColor(Color("#1B2028"))
        height(44.px)
        padding(10.px, 10.px)
        property("border-bottom", "2px solid rgba(255,255,255,0.22)")
        property("border-right", "1px solid rgba(255,255,255,0.07)")
        fontSize(13.px)
    }

    val filterRow by style {
        position(Position.Sticky)
        top(32.px)
        height(32.px)
        property("z-index", "9")
        backgroundColor(Color("#1A1F2D"))
        property("box-sizing", "border-box")
    }

    val smartFilterRow by style {
        backgroundColor(Color("#1B2028"))
        height(42.px)
    }

    val filterCell by style {
        position(Position.Sticky)
        top(32.px)
        property("z-index", "9")
        backgroundColor(Color("#1A1F2D"))
        property("box-sizing", "border-box")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
    }

    val smartFilterCell by style {
        backgroundColor(Color("#1B2028"))
        property("border-bottom", "2px solid rgba(255,255,255,0.16)")
        property("border-right", "1px solid rgba(255,255,255,0.07)")
    }

    val td by style {
        padding(2.px, 8.px)
        height(28.px)
        fontSize(12.px)
        whiteSpace("nowrap")
        property("vertical-align", "middle")
        property("border-bottom", "1px solid rgba(255,255,255,0.05)")
        property("border-right", "1px solid rgba(255,255,255,0.05)")
        color(Color.white)
        backgroundColor(Color("#373B40"))
        property("box-sizing", "border-box")
        property("transition", "background-color 0.12s ease, border-color 0.12s ease")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }

    val row by style {
        property("transition", "background 0.1s")
        self + ":nth-of-type(even) td" style {
            backgroundColor(Color("#3F454E"))
        }
    }

    val smartRow by style {
        self + " td" style {
            backgroundColor(Color.transparent)
        }
        self + ":nth-of-type(even) td" style {
            backgroundColor(Color.transparent)
        }
        self + " td:last-child" style {
            property("border-right", "1px solid transparent")
        }
    }

    val tr by style {
        property("transition", "background 0.1s")
    }

    val smartTd by style {
        backgroundColor(Color.transparent)
        height(44.px)
        padding(8.px, 10.px)
        fontSize(13.px)
        property("border-bottom", "2px solid rgba(255,255,255,0.18)")
        property("border-right", "1px solid rgba(255,255,255,0.07)")
    }

    val input by style {
        border(0.px)
        outline("none")
        backgroundColor(Color.transparent)
        fontSize(12.px)
        width(100.percent)
        minWidth(0.px)
        height(100.percent)
        margin(0.px)
        padding(0.px)
        display(DisplayStyle.Block)
        color(Color.white)
        property("box-sizing", "border-box")
    }

    val infoText by style {
        fontSize(11.px)
        color(Color("#9AA4B5"))
        whiteSpace("nowrap")
    }

    val button by style {
        applyAppButtonChrome()
        padding(4.px, 12.px)
        self + hover style {
            applyAppButtonHoverChrome()
        }
        active {
            applyAppButtonActiveChrome()
        }
    }

    val columnToggleHeader by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        fontWeight("bold")
        marginBottom(6.px)
        paddingBottom(4.px)
        property("border-bottom", "1px solid #303645")
        color(Color.white)
        fontSize(13.px)
    }

    val columnToggleItem by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        padding(2.px, 0.px)
        whiteSpace("nowrap")
        color(Color("#E0E4EB"))
        fontSize(12.px)
    }

    val select by style {
        backgroundColor(Color.transparent)
        color(Color.white)
        border(0.px)
        padding(2.px, 4.px)
        cursor("pointer")
    }

    val option by style {
        backgroundColor(Color("#171a20"))
        color(Color.white)
    }

    val scrollSnapCell by style {
        property("scroll-snap-align", "none")
    }

    val topBarButton by style {
        flexShrink(0)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        height(100.percent)
        padding(0.px, 10.px)
        fontSize(13.px)
        color(Color.white)
        backgroundColor(Color.transparent)
        border(0.px)
        cursor("pointer")
        property("user-select", "none")
        property("box-shadow", "none")
    }
    val topBarIcon by style { width(18.px); height(18.px); marginRight(6.px); flexShrink(0) }

    val cellIcon by style {
        width(22.px)
        height(22.px)
        property("filter", "brightness(0) invert(1)")
    }
    val topBarCounter by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        fontSize(12.px)
        color(Color.white)
        marginLeft(8.px)
        whiteSpace("nowrap")
        property("flex-shrink", "0")
        cursor("pointer")
    }
    val topBarCounterIcon by style {
        width(18.px)
        height(18.px)
        property("flex-shrink", "0")
        property("filter", "brightness(0) invert(1)")
    }
    val quickDivider by style {
        width(1.px)
        height(16.px)
        backgroundColor(Color("rgba(255,255,255,0.2)"))
    }
}

private fun tabularHttpUrl(value: String): String? {
    val clean = value.trim()
    return when {
        clean.startsWith("https://", ignoreCase = true) -> clean
        clean.startsWith("http://", ignoreCase = true) -> clean
        clean.startsWith("www.", ignoreCase = true) -> "https://$clean"
        clean.equals("linkedin.com", ignoreCase = true) || clean.startsWith("linkedin.com/", ignoreCase = true) -> "https://www.$clean"
        else -> null
    }
}

class CellColor(
    val color: CSSColorValue? = null,
    val outlineCss: String? = null,
    val animationCss: String? = null
)

object TabularSelectionMode { const val ROWS = "ROWS"; const val CELLS = "CELLS" }

// TabularCellRange replaced by All: startRow=lfdNr, startCol=stufe, endRow=kapazitaetCcg1, endCol=kapazitaetCcg2
typealias TabularCellRange = org.example.project.utils.All
fun TabularCellRange(startRow: Int, startCol: Int, endRow: Int, endCol: Int): TabularCellRange =
    org.example.project.utils.All(lfdNr = startRow, stufe = startCol, kapazitaetCcg1 = endRow, kapazitaetCcg2 = endCol)

val TabularCellRange.topRow: Int get() = minOf(lfdNr, kapazitaetCcg1)
val TabularCellRange.bottomRow: Int get() = maxOf(lfdNr, kapazitaetCcg1)
val TabularCellRange.leftCol: Int get() = minOf(stufe, kapazitaetCcg2)
val TabularCellRange.rightCol: Int get() = maxOf(stufe, kapazitaetCcg2)

fun TabularCellRange.contains(row: Int, col: Int): Boolean =
    row in topRow..bottomRow && col in leftCol..rightCol

@Composable
fun EditableTableCell(
    id: String,
    colIndex: Int,
    cellValue: String,
    isEditable: Boolean,
    isToggleCell: Boolean = false,
    dropdownOptions: List<String>?,
    showCalendarPopup: Boolean = false,
    multiline: Boolean = false,
    backgroundColor: CellColor?,
    onCellEdit: ((String, Int, String) -> Unit)? = null,
    onCellClick: ((String, Int) -> Unit)? = null,
    onCellDoubleClick: ((String, Int) -> Unit)? = null,
    onCellMouseDown: ((String, Int) -> Unit)? = null,
    onCellMouseEnter: ((String, Int) -> Unit)? = null,
    onCellMouseLeave: ((String, Int) -> Unit)? = null,
    onCellMouseMove: ((String, Int, Double, Double) -> Unit)? = null,
    onCellClickPosition: ((String, Int, Double, Double) -> Unit)? = null,
    onCellMouseUp: ((String, Int) -> Unit)? = null,
    onCellContextMenu: ((String, Int, Double, Double) -> Unit)? = null,
    isHoveredRow: Boolean,
    isSelectedRow: Boolean,
    isRowRangeSelected: Boolean = false,
    isSelectedCell: Boolean = false,
    isInSelectedRange: Boolean = false,
    isRangeTopEdge: Boolean = false,
    isRangeBottomEdge: Boolean = false,
    isRangeLeftEdge: Boolean = false,
    isRangeRightEdge: Boolean = false,
    startEditingOnSingleClick: Boolean = true,
    rowHeightCss: String? = null,
    cellFontSizeCss: String? = null,
    dividerColorCss: String? = null,
    dataColumnWidthCss: String? = null,
    selectionOutlineOnly: Boolean = false,
    suppressRowHighlight: Boolean = false,
    smartMode: Boolean = false
) {
    var isEditing by remember { mutableStateOf(false) }
    var editedText by remember(cellValue) { mutableStateOf(cellValue) }
    var skipBlurCommit by remember { mutableStateOf(false) }
    var isCalendarOpen by remember { mutableStateOf(false) }
    val linkUrl = tabularHttpUrl(cellValue)

    val finalColor = when {
        backgroundColor != null -> backgroundColor.color
        selectionOutlineOnly && (isSelectedCell || isInSelectedRange) -> null
        suppressRowHighlight -> null
        isRowRangeSelected -> UISettings.TABULAR_HOVER_BG
        isSelectedCell || isInSelectedRange -> UISettings.TABULAR_SELECTED_BG
        isSelectedRow -> UISettings.TABULAR_SELECTED_BG
        isHoveredRow -> UISettings.TABULAR_HOVER_BG
        else -> null
    }

    Td({
        classes(TableStyles.td, TableStyles.scrollSnapCell)
        if (smartMode) {
            classes(TableStyles.smartTd)
        }
        style {
            finalColor?.let { backgroundColor(it) }
            if (multiline) { property("white-space", "pre-wrap"); property("overflow-wrap", "anywhere") }
            rowHeightCss?.let { property("height", it) }
            cellFontSizeCss?.let { property("font-size", it) }
            dataColumnWidthCss?.let {
                property("width", it)
                property("min-width", it)
                property("max-width", it)
            }
            dividerColorCss?.let {
                property("border-right", "1px solid $it")
                property("border-bottom", "1px solid $it")
            }
            if (dropdownOptions != null || showCalendarPopup) {
                position(Position.Relative)
            }
            if (linkUrl != null || cellValue == TABULAR_DOCUMENT_ICON_VALUE || cellValue == TABULAR_MINE_CONTENT_ICON_VALUE || cellValue == TABULAR_CALENDAR_ICON_VALUE) {
                cursor("pointer")
            }
            if (showCalendarPopup) {
                overflow("visible")
                property("z-index", "2")
            }
            val insetShadows = buildList {
                backgroundColor?.outlineCss?.takeIf { it.isNotBlank() }?.let { add(it) }
                if (isSelectedCell || isInSelectedRange) {
                    if (selectionOutlineOnly && isSelectedCell && !isInSelectedRange) {
                        add("inset 0 0 0 2px ${UISettings.ACCENT}")
                    } else {
                        if (isRangeTopEdge) add("inset 0 2px 0 0 ${UISettings.ACCENT}")
                        if (isRangeBottomEdge) add("inset 0 -2px 0 0 ${UISettings.ACCENT}")
                        if (isRangeLeftEdge) add("inset 2px 0 0 0 ${UISettings.ACCENT}")
                        if (isRangeRightEdge) add("inset -2px 0 0 0 ${UISettings.ACCENT}")
                    }
                }
            }
            if (insetShadows.isNotEmpty()) {
                property("box-shadow", insetShadows.joinToString(", "))
                if (selectionOutlineOnly) {
                    property("border-bottom", "1px solid ${dividerColorCss ?: "rgba(255,255,255,0.05)"}")
                    property("border-right", "1px solid ${dividerColorCss ?: "rgba(255,255,255,0.05)"}")
                }
            } else if (!suppressRowHighlight && (isRowRangeSelected || isHoveredRow || isSelectedRow)) {
                property("border-bottom", "1px solid transparent")
                property("border-right", "1px solid transparent")
                property("box-shadow", "none")
            } else {
                property("box-shadow", "none")
            }
            backgroundColor?.animationCss?.takeIf { it.isNotBlank() }?.let {
                property("animation", it)
            }
        }

        tabIndex(0)
        attr("data-row", id)
        attr("data-col", colIndex.toString())

        onClick {
            val mouseEvent = it.nativeEvent as? MouseEvent
            onCellClickPosition?.invoke(
                id,
                colIndex,
                mouseEvent?.clientX?.toDouble() ?: 0.0,
                mouseEvent?.clientY?.toDouble() ?: 0.0
            )
            if (isToggleCell && isEditable) {
                onCellEdit?.invoke(
                    id,
                    colIndex,
                    if (cellValue == "\u2705") "\u2B1C" else "\u2705"
                )
            } else if (showCalendarPopup && isEditable) {
                isCalendarOpen = true
            } else if (isEditable && (startEditingOnSingleClick || isSelectedCell)) {
                isEditing = true
            }
            onCellClick?.invoke(id, colIndex)
        }
        onDoubleClick {
            if (isEditable && !startEditingOnSingleClick) isEditing = true
            onCellDoubleClick?.invoke(id, colIndex)
        }
        onMouseDown {
            val button = (it.nativeEvent as? MouseEvent)?.button
            if (button?.toInt() == 0) {
                onCellMouseDown?.invoke(id, colIndex)
            }
        }
        onMouseEnter {
            onCellMouseEnter?.invoke(id, colIndex)
        }
        onMouseLeave {
            onCellMouseLeave?.invoke(id, colIndex)
        }
        onMouseMove {
            val mouseEvent = it.nativeEvent as? MouseEvent
            onCellMouseMove?.invoke(
                id,
                colIndex,
                mouseEvent?.clientX?.toDouble() ?: 0.0,
                mouseEvent?.clientY?.toDouble() ?: 0.0
            )
        }
        onMouseUp {
            val button = (it.nativeEvent as? MouseEvent)?.button
            if (button?.toInt() == 0) {
                onCellMouseUp?.invoke(id, colIndex)
            }
        }
        onContextMenu {
            onCellContextMenu?.let { callback ->
                it.preventDefault()
                val mouseEvent = it.nativeEvent as? MouseEvent
                callback(
                    id,
                    colIndex,
                    mouseEvent?.clientX?.toDouble() ?: 0.0,
                    mouseEvent?.clientY?.toDouble() ?: 0.0
                )
            }
        }

        onKeyDown { event ->
            if (event.key == "Tab") {
                event.preventDefault()
                val nextCol = if (event.shiftKey) colIndex - 1 else colIndex + 1
                val target = document.querySelector("[data-row='$id'][data-col='$nextCol']") as? HTMLElement
                if (target != null) {
                    target.focus()
                    target.click()
                }
            } else if (isToggleCell && isEditable && (event.key == "Enter" || event.key == " ")) {
                event.preventDefault()
                onCellEdit?.invoke(
                    id,
                    colIndex,
                    if (cellValue == "\u2705") "\u2B1C" else "\u2705"
                )
            } else if (event.key == "Enter" && isEditable && showCalendarPopup) {
                isCalendarOpen = true
                event.preventDefault()
            } else if (event.key == "Enter" && isEditable) {
                isEditing = true
                event.preventDefault()
            }
        }
    }) {
        if (isToggleCell) {
            if (cellValue.isBlank()) {
                Text("")
            } else {
                LumaticaCheckbox(
                    checked = cellValue == "\u2705",
                    onChange = { checked ->
                        if (isEditable) onCellEdit?.invoke(id, colIndex, if (checked) "\u2705" else "\u2B1C")
                    },
                    enabled = isEditable
                )
            }
        } else if (cellValue.startsWith("::button::")) {
            LumaticaButton(
                label = cellValue.removePrefix("::button::"),
                onClick = {},
                variant = LumaticaButtonVariant.Standard,
                heightPx = 32,
                fillWidth = true
            )
        } else if (dropdownOptions != null) {
            AppDropdown(
                options = dropdownOptions.map { All(key = it, label = it) },
                selectedValue = cellValue,
                onChange = {
                    if (isEditable) onCellEdit?.invoke(id, colIndex, it)
                },
                placeholder = dropdownOptions.firstOrNull() ?: "Auswahl",
                enabled = isEditable,
                appearance = AppDropdownAppearance.Field,
                menuMode = org.example.project.settings.AppDropdownMenuMode.Overlay,
                attrs = {
                    classes(TableStyles.select)
                    if (!isEditable) {
                        style { cursor("default") }
                    } else {
                        style {
                            width(100.percent)
                            minHeight(24.px)
                        }
                    }
                }
            )
        } else if (showCalendarPopup) {
            Div({
                style {
                    width(100.percent)
                    minHeight(24.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    property("cursor", if (isEditable) "pointer" else "default")
                }
            }) {
                Text(cellValue)
            }
            if (isCalendarOpen) {
                Div({
                    style {
                        position(Position.Absolute)
                        left(0.px)
                        top(100.percent)
                        marginTop(6.px)
                        padding(10.px)
                        property("z-index", "30")
                        property("background", "rgba(23,25,29,0.96)")
                        property("border", "1px solid rgba(255,255,255,0.10)")
                        property("border-radius", "16px")
                        property("box-shadow", "0 12px 28px rgba(0,0,0,0.38)")
                    }
                    onClick { it.stopPropagation() }
                }) {
                    val selectedIso = remember(cellValue) {
                        val normalized = cellValue.trim()
                        when {
                            normalized.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) -> normalized
                            normalized.matches(Regex("\\d{2}\\.\\d{2}\\.\\d{4}")) -> {
                                val parts = normalized.split(".")
                                "${parts[2]}-${parts[1]}-${parts[0]}"
                            }
                            else -> kalenderTodayIsoDate()
                        }
                    }
                    EngineMonthCalendar(
                        selectedDate = selectedIso,
                        onSelectDate = {
                            onCellEdit?.invoke(id, colIndex, it)
                            isCalendarOpen = false
                        },
                        showFooter = false,
                        useWeekBorderLayout = false,
                        showSelectedDayBorder = false
                    )
                }
            }
        } else if (cellValue == TABULAR_DOCUMENT_ICON_VALUE || cellValue == TABULAR_MINE_CONTENT_ICON_VALUE || cellValue == TABULAR_CALENDAR_ICON_VALUE) {
            Div({
                style {
                    width(100.percent)
                    height(100.percent)
                    minHeight(24.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                }
            }) {
                Img(src = tabularIconPath(cellValue), attrs = {
                    classes(TableStyles.cellIcon)
                })
            }
        } else if (isEditing && multiline) {
            TextArea(attrs = {
                classes(TableStyles.input)
                value(editedText)
                style { minHeight(120.px); property("resize", "vertical"); property("white-space", "pre-wrap") }
                ref { element -> element.focus(); onDispose { } }
                onInput { editedText = it.value }
                onKeyDown { event ->
                    event.stopPropagation()
                    if (event.key == "Escape") { skipBlurCommit = true; isEditing = false }
                    if (event.key == "Enter" && (event.ctrlKey || event.metaKey)) {
                        event.preventDefault()
                        skipBlurCommit = true
                        isEditing = false
                        onCellEdit?.invoke(id, colIndex, editedText)
                    }
                }
                onBlur {
                    isEditing = false
                    if (skipBlurCommit) skipBlurCommit = false else onCellEdit?.invoke(id, colIndex, editedText)
                }
            })
        } else if (isEditing) {
            var inputRef by remember { mutableStateOf<HTMLInputElement?>(null) }

            SideEffect {
                inputRef?.focus()
                inputRef?.setSelectionRange(editedText.length, editedText.length)
            }

            Input(InputType.Text, attrs = {
                classes(TableStyles.input)
                value(editedText)
                ref {
                    inputRef = it
                    onDispose { inputRef = null }
                }
                onInput { editedText = it.value }
                onKeyDown { event ->
                    if (event.key == "Enter" || event.key == "Tab") {
                        skipBlurCommit = true
                        isEditing = false
                        onCellEdit?.invoke(id, colIndex, editedText)

                        if (event.key == "Tab") {
                            event.preventDefault()
                            val nextCol = if (event.shiftKey) colIndex - 1 else colIndex + 1
                            val target = document.querySelector("[data-row='$id'][data-col='$nextCol']") as? HTMLElement
                            if (target != null) {
                                window.setTimeout({
                                    target.focus()
                                    target.click()
                                }, 0)
                            }
                        }
                    }
                }
                onBlur {
                    isEditing = false
                    if (skipBlurCommit) {
                        skipBlurCommit = false
                    } else {
                        onCellEdit?.invoke(id, colIndex, editedText)
                    }
                }
            })
        } else if (linkUrl != null) {
            A(href = linkUrl, attrs = {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
                attr("title", "Link in neuem Tab öffnen")
                style {
                    color(Color("#66aaff"))
                    property("text-decoration", "underline")
                    property("text-underline-offset", "2px")
                    property("cursor", "pointer")
                }
                onClick { it.stopPropagation() }
                onDoubleClick { it.stopPropagation() }
            }) {
                Text(if (linkUrl.startsWith("https://www.google.com/maps/search/?api=1&query=")) "Google Maps" else cellValue)
            }
        } else {
            Text(cellValue)
        }
    }
}

const val TABULAR_DOCUMENT_ICON_VALUE = "__TABULAR_DOCUMENT_ICON__"
const val TABULAR_MINE_CONTENT_ICON_VALUE = "__TABULAR_MINE_CONTENT_ICON__"
const val TABULAR_CALENDAR_ICON_VALUE = "__TABULAR_CALENDAR_ICON__"

private fun tabularIconPath(value: String): String = when (value) {
    TABULAR_MINE_CONTENT_ICON_VALUE -> "images/box.svg"
    TABULAR_CALENDAR_ICON_VALUE -> "images/kalender.svg"
    else -> "images/dokumente.svg"
}
